import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import Input from "@/components/forms/Input";
import Button from "@/components/button/Button";
import { Pencil } from "lucide-react";
import type { DateFilterModalProps } from "./DateFilterModal.types.tsx";

const DateFilterModal: React.FC<DateFilterModalProps> = (
    {
        open,
        onClose,
        value,
        onChange,
        defaultStart,
        defaultEnd,
    }) => {
    const [tempStart, setTempStart] = useState("");
    const [tempEnd, setTempEnd] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setTempStart(value?.startDate || defaultStart || "");
            setTempEnd(value?.endDate || defaultEnd || "");
            setErrorMessage(null);
            setIsEditing(!value);
        }
    }, [open, value, defaultStart, defaultEnd]);

    const handleConfirm = () => {
        if (!tempStart || !tempEnd) {
            setErrorMessage("Veuillez sélectionner les dates de début et de fin.");
            return;
        }

        if (tempStart > tempEnd) {
            setErrorMessage("La date de début doit être avant la date de fin.");
            return;
        }

        onChange({ startDate: tempStart, endDate: tempEnd });
        onClose();
    };

    const handleClear = () => {
        onChange(null);
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Filtre de dates"
            size="sm"
            footer={
                <>
                    <button
                        onClick={onClose}
                        className="inline-flex items-center rounded-full bg-gray-200 cursor-pointer px-4 py-2 font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleClear}
                        disabled={!value}
                        className={`inline-flex items-center rounded-full bg-red-500 px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${value ? "cursor-pointer hover:bg-red-600" : "opacity-50 cursor-not-allowed"}`}
                    >
                        Effacer
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="inline-flex items-center rounded-full bg-green-600 px-4 py-2 font-medium text-white cursor-pointer hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                        Confirmer
                    </button>
                </>
            }
        >
            <div className="text-gray-700">
                {isEditing ? (
                    <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2">
                            <label htmlFor="filter-start-date" className="text-sm font-medium text-gray-700">Du</label>
                            <Input
                                id="filter-start-date"
                                type="date"
                                value={tempStart}
                                onChange={(e) => {
                                    setTempStart(e.target.value);
                                    setErrorMessage(null);
                                }}
                                className="w-40"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <label htmlFor="filter-end-date" className="text-sm font-medium text-gray-700">au</label>
                            <Input
                                id="filter-end-date"
                                type="date"
                                value={tempEnd}
                                onChange={(e) => {
                                    setTempEnd(e.target.value);
                                    setErrorMessage(null);
                                }}
                                className="w-40"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2 py-2">
                        <span className="text-xl font-bold">{tempStart} à {tempEnd}</span>
                        <Button type="primary" icon={<Pencil size={16}/>} onClick={() => setIsEditing(true)}/>
                    </div>
                )}
                {errorMessage && (
                    <div className="mt-3 text-center text-sm text-red-600 font-medium">
                        {errorMessage}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default DateFilterModal;
